import React, { useState, useEffect } from "react";
import { EMAILREGEX, CONTACTPHONEREGEX, NAMEREGEX } from "@client/constants/regex";
import Modal from "react-modal";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { submitBulkContactForm } from "@actions/bulkContact";
import { ClearedStyleInput } from "@modules/Components";
import SubmitButton from "@components/SubmitButton";
import ReCaptchaService from "@modules/ServiceManager/Services/ReCaptchaService";
import { initializeRecaptcha } from "@actions/accountActions";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const InputSection = styled.div``;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-gap: 10px;

  @media (max-width: 550px) {
    grid-gap: 0px;
  }

  @media (max-width: 450px) {
    display: block;
  }
`;

const InputDiv = styled.div`
  width: 100%;
  margin: 0 auto;
  border-radius: 5px;
  box-shadow: 0 20px 70px 0 rgba(34, 35, 36, 0.12);
  background-color: #ffffff;
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  padding: 0;
  height: 72px;
  font-size: 16px;
  position: relative;

  @media (max-width: 485px) {
    height: 63px;
  }
`;

const InputContainer = styled.div`
  border-radius: 5px;
  height: 100%;
  width: 100%;
  align-items: center;
  display: flex;
  padding: 0px;

  @media (max-width: 485px) {
    font-size: 14px;
  }
`;

const ErrorMessageInner = styled.div`
  color: #ee5050;
  font-size: 10px;
  letter-spacing: 1px;
  position: absolute;
  font-weight: 900;
  top: 3px;
  left: 25px;
`;

const InputField = styled(ClearedStyleInput)`
  border: ${props => (props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : "")};
  color: #000000;
  opacity: 1;
  padding-left: 25px;
  height: 100%;
  width: 100%;
  border-radius: 5px;
  padding-top: 0px;
  padding-bottom: 0px;
  text-overflow: ellipsis;

  &:focus {
    border: ${props =>
      props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : "solid 2px #1f2021"};
    outline: none;
  }
  &:hover {
    border: ${props =>
      props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : ""};
    outline: none;
  }
  &::placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
  &::-webkit-input-placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
  &:-ms-input-placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
`;

const InputTextArea = styled.textarea`
  border: ${props =>
    props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : "solid 1px #ffffff"};
  box-shadow: 0 20px 70px 0 rgba(34, 35, 36, 0.12);
  color: #000000;
  opacity: 1;
  padding-left: 25px;
  padding-right: 25px;
  height: 150px;
  width: 100%;
  border-radius: 5px;
  padding-top: 20px;
  padding-bottom: 0px;
  resize: vertical;
  text-overflow: ellipsis;

  &:focus {
    border: ${props =>
      props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : "solid 2px #1f2021"};
    outline: none;
  }
  &:hover {
    border: ${props =>
      props.error ? "solid 1px rgba(238, 80, 80, 0.48)" : ""};
    outline: none;
  }
  &::placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
  &::-webkit-input-placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
  &:-ms-input-placeholder {
    color: rgba(31, 32, 33, 0.65);
  }
`;

const RecaptchaDiv = styled.div`
  margin-top: 110px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 400px) {
    transform: scale(0.77);
  }
`;

const CloseDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  color: #9b9b9b;
  padding-right: 8px;
  padding-top: 8px;
  align-self: flex-start;
  margin-bottom: -30px;
`;

const CloseSpan = styled.span`
  cursor: pointer;
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CloseDivImage = styled.img`
  width: 18px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 500;
  text-align: center;
  color: #1f2021;
`;

const Container = styled.div`
  width: 100%;
  max-width: 579px;
  height: auto;
  border-radius: 30px;
  background-color: #ffffff;
  margin: 0 auto;
  position: relative;

  @media (max-height: 975px) {
    margin-top: 63px;
  }
  @media (max-height: 900px) {
    margin-top: 50px;
  }
  @media (max-height: 715px) {
    margin-top: 25px;
  }
  @media (max-width: 565px) {
    margin-top: 20px;
    padding: 12px;
  }
  @media (max-width: 400px) {
    margin-top: 2px;
  }
`;

const InnerContainer = styled.div`
  max-width: 495px;
  margin: 0 auto;
  border-radius: 30px;
  padding: 20px;
`;

const ErrorText = styled.div`
  color: #d9534f;
  font-size: 12px;
  text-align: center;
  margin-top: 15px;
`;

const ThankYouMessage = styled.div`
  color: #1f2021;
  text-align: center;
  padding: 40px 20px;
`;

const customModalStyles = {
  content: {
    maxWidth: "720px",
    maxHeight: "95vh",
    width: "95%",
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: "0px",
    borderRadius: "24px",
    position: "relative",
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    overflow: "auto",
    "&::WebkitScrollbar": {
      display: "none"
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none"
  },
  overlay: {
    backgroundColor: "rgba(31, 32, 33, 0.8)",
    zIndex: 2000,
    border: "none"
  }
};

const ContactModalForm = ({
  isOpen,
  onRequestClose,
  prefill = {},
  adSource
}) => {
  const [email, setEmail] = useState(prefill.email || "");
  const [company, setCompany] = useState(prefill.company || "");
  const [firstName, setFirstName] = useState(prefill.firstName || "");
  const [lastName, setLastName] = useState(prefill.lastName || "");
  const [phone, setPhone] = useState(prefill.phone || "");
  const [message, setMessage] = useState(prefill.message || "");
  const page = prefill.page || null;
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const [recaptchaCode, setRecaptchaCode] = useState("");
  const recaptchaTarget = "recaptchaTargetContactModalForm";

  const setRecaptcha = data => {
    setRecaptchaCode(data);
  };

  const setUpRecaptcha = () => {
    window.setTimeout(() => {
      const container = document.getElementById(recaptchaTarget);
      if (!container) return;
      grecaptcha.render(document.getElementById(recaptchaTarget), {
        sitekey: "6LdD7SgTAAAAALjVAm80uYL-CKmPlF1Wwt0CYjv4",
        callback: data => {
          setRecaptcha(data);
        }
      });
    }, 1000);
  };

  const getRecaptchaService = async () => {
    await dispatch(initializeRecaptcha(new ReCaptchaService()));
    setUpRecaptcha();
  };

  useEffect(() => {
    if (!globalThis?.grecaptcha) {
      getRecaptchaService();
    } else {
      setUpRecaptcha();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSent(false);
      setSubmitting(false);
      setErrors({});
    }
  }, [isOpen]);

  const localHandleChange = (e, setter, field) => {
    setter(e.target.value);
    const errs = validate();
    setErrors(errs);
  };

  const validate = () => {
    const errs = {};
    if (!email || !EMAILREGEX.test(email)) {
      errs.email = "Please enter a valid email.";
    }
    if (!firstName || !NAMEREGEX.test(firstName)) {
      errs.firstName = "Please enter a valid first name.";
    }
    if (!lastName || !NAMEREGEX.test(lastName)) {
      errs.lastName = "Please enter a valid last name.";
    }
    if (!company) {
      errs.company = "Please enter a company name.";
    }
    if (!phone || !CONTACTPHONEREGEX.test(phone)) {
      errs.phone = "Please enter a valid phone number.";
    }
    if (!message) {
      errs.message = "Please enter a message.";
    }
    if (!recaptchaCode) {
      errs.recaptcha = "Please complete the reCAPTCHA verification.";
    }
    return errs;
  };

  const [isReadyForSubmit, setIsReadyForSubmit] = useState(false);

  useEffect(() => {
    const errs = validate();
    setIsReadyForSubmit(Object.keys(errs).length === 0);
  }, [email, company, firstName, lastName, phone, message, recaptchaCode]);

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      const payload = {
        email,
        company,
        first_name: firstName,
        last_name: lastName,
        phone_number: phone,
        message,
        page,
        ad_source: adSource
      };
      if (recaptchaCode) payload.recaptcha_token = recaptchaCode;
      if (page === null) delete payload.page;
      const result = await dispatch(submitBulkContactForm(payload));
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
      if (!result.success) {
        setErrors({
          submit: "We were unable to send your message. Please try again."
        });
        return;
      }
      setSent(true);
      (window.dataLayer || []).push({
        event: page === "Buy" ? "bulkBuyFormSubmission" : "bulkSellFormSubmission"
      });
      setEmail("");
      setCompany("");
      setFirstName("");
      setLastName("");
      setPhone("");
      setMessage("");
      setRecaptchaCode("");
    } catch (error) {
      setErrors({
        submit: "We were unable to send your message. Please try again."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Contact Us"
      shouldCloseOnOverlayClick={!submitting}
      style={customModalStyles}
    >
      <Container>
        <CloseDiv>
          <CloseSpan onClick={onRequestClose}>
            <CloseDivImage
              src="https://cdn.cardcash.com/website/ui/elements/close.svg"
              aria-hidden="true"
              alt="close button"
            />
          </CloseSpan>
        </CloseDiv>
        <InnerContainer>
          <Title>Contact Us</Title>
          {!sent ? (
            <Form onSubmit={handleSubmit}>
              <InputSection>
                <InputGrid>
                  <InputDiv>
                    <InputContainer>
                      <InputField
                        error={errors.firstName}
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={firstName}
                        onChange={e =>
                          localHandleChange(e, setFirstName, "firstName")
                        }
                      />
                    </InputContainer>
                    {errors.firstName && (
                      <ErrorMessageInner className={"animated fadeIn"}>
                        {errors.firstName}
                      </ErrorMessageInner>
                    )}
                  </InputDiv>

                  <InputDiv>
                    <InputContainer>
                      <InputField
                        error={errors.lastName}
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={lastName}
                        onChange={e =>
                          localHandleChange(e, setLastName, "lastName")
                        }
                      />
                    </InputContainer>
                    {errors.lastName && (
                      <ErrorMessageInner className={"animated fadeIn"}>
                        {errors.lastName}
                      </ErrorMessageInner>
                    )}
                  </InputDiv>
                </InputGrid>

                <InputDiv>
                  <InputContainer>
                    <InputField
                      error={errors.email}
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={e => localHandleChange(e, setEmail, "email")}
                    />
                  </InputContainer>
                  {errors.email && (
                    <ErrorMessageInner className={"animated fadeIn"}>
                      {errors.email}
                    </ErrorMessageInner>
                  )}
                </InputDiv>

                <InputGrid>
                  <InputDiv>
                    <InputContainer>
                      <InputField
                        error={errors.company}
                        name="company"
                        placeholder="Company Name"
                        value={company}
                        onChange={e =>
                          localHandleChange(e, setCompany, "company")
                        }
                      />
                    </InputContainer>
                    {errors.company && (
                      <ErrorMessageInner className={"animated fadeIn"}>
                        {errors.company}
                      </ErrorMessageInner>
                    )}
                  </InputDiv>

                  <InputDiv>
                    <InputContainer>
                      <InputField
                        error={errors.phone}
                        name="phone"
                        placeholder="+1 (555) 555-5555"
                        value={phone}
                        onChange={e => localHandleChange(e, setPhone, "phone")}
                      />
                    </InputContainer>
                    {errors.phone && (
                      <ErrorMessageInner className={"animated fadeIn"}>
                        {errors.phone}
                      </ErrorMessageInner>
                    )}
                  </InputDiv>
                </InputGrid>

                <InputDiv>
                  <InputContainer>
                    <InputTextArea
                      error={errors.message}
                      name="message"
                      placeholder={prefill.messagePlaceholder}
                      value={message}
                      onChange={e =>
                        localHandleChange(e, setMessage, "message")
                      }
                    />
                  </InputContainer>
                  {errors.message && (
                    <ErrorMessageInner className={"animated fadeIn"}>
                      {errors.message}
                    </ErrorMessageInner>
                  )}
                </InputDiv>

                <RecaptchaDiv id={recaptchaTarget} />

                {errors.submit && <ErrorText>{errors.submit}</ErrorText>}

                <div style={{ marginTop: 35, marginBottom: 40 }}>
                  <SubmitButton
                    type="submit"
                    disabled={submitting || !isReadyForSubmit}
                    styles={{ height: "64px", borderRadius: "36px" }}
                    disabledBackGroundColor={"rgba(119, 121, 132, 0.48)"}
                  >
                    {submitting ? "Sending..." : "Send"}
                  </SubmitButton>
                </div>
              </InputSection>
            </Form>
          ) : (
            <ThankYouMessage>
              Thank you — we received your message. We will be in touch soon.
            </ThankYouMessage>
          )}
        </InnerContainer>
      </Container>
    </Modal>
  );
};

export default ContactModalForm;
